import type { Cell, Grid } from '../puzzle';

/** safeTop / safeBottom：env(safe-area-inset-*)（CSS px），刘海与 Home 条 */
export type Viewport = { width: number; height: number; safeTop?: number; safeBottom?: number };
/** 世界坐标：单位 = CSS 像素，原点在画布中心，y 向上。w × h = 图（棋盘）在世界里的宽高 */
export type BoardRect = { cx: number; cy: number; w: number; h: number };
export type Rect = { x0: number; y0: number; x1: number; y1: number };

/** 顶部：毛毡顶栏 96px（header.png 192px@2x）+ 数据胶囊行 35px + 间距 */
export const HUD_TOP = 146;
/** 底部：80px 方形毛毡按钮 + 底边距 + 间距 */
export const HUD_BOTTOM = 112;
/** 纸板框外沿到屏幕左右边的留白 */
export const SIDE = 8;
/**
 * 纸板框（public/ui/frame.png，520px@2x，九宫格 64px）外沿到透明内沿实测 38–39 图像像素 ≈ 19.25 CSS px。
 * 取 18：框的深色内沿正好压住棋盘外沿约 1px，不露缝。棋盘尺寸据此扣除，框位置由 frameRect 从 boardRect 推出。
 */
export const FRAME_INSET = 18;

/**
 * 棋盘（= 图）在世界里的矩形。aspect = 图的宽 / 高（城市 1，塔罗竖版 2/3）。
 * 宽高同时受可用宽度与可用高度约束；竖版图在 390×844 上是宽度先顶满，所以高度用掉更多竖向空间，
 * 格子比同块数的方形棋盘大。方形（aspect = 1）时与以前逐像素相同。
 */
export function boardRect(vp: Viewport, aspect = 1): BoardRect {
  const room = FRAME_INSET * 2;
  const top = HUD_TOP + (vp.safeTop ?? 0);
  const bottom = HUD_BOTTOM + (vp.safeBottom ?? 0);
  const availW = vp.width - SIDE * 2 - room;
  const availH = vp.height - top - bottom - room;
  let w: number;
  let h: number;
  if (aspect === 1) {
    w = h = Math.max(120, Math.floor(Math.min(availW, availH)));
  } else {
    w = Math.max(120, Math.floor(Math.min(availW, availH * aspect)));
    h = Math.floor(w / aspect);
  }
  return { cx: 0, cy: (bottom - top) / 2, w, h };
}

/** 纸板框 DOM 叠层在屏幕坐标（CSS px，左上原点）中的位置：棋盘四周各外扩 FRAME_INSET */
export function frameRect(vp: Viewport, r: BoardRect): { left: number; top: number; width: number; height: number } {
  const tl = worldToScreen(vp, r.cx - r.w / 2, r.cy + r.h / 2);
  return { left: tl.x - FRAME_INSET, top: tl.y - FRAME_INSET, width: r.w + FRAME_INSET * 2, height: r.h + FRAME_INSET * 2 };
}

/** 方形棋盘可以直接传边长 n */
export type GridArg = Grid | number;
export const asGrid = (g: GridArg): Grid => (typeof g === 'number' ? { cols: g, rows: g } : g);

/** 格子宽高（长方形棋盘的格子不一定正方：竖版 5×7 约 0.93、7×10 约 0.95） */
export function cellSize(r: BoardRect, grid: GridArg): { w: number; h: number } {
  const g = asGrid(grid);
  return { w: r.w / g.cols, h: r.h / g.rows };
}

export function cellRect(r: BoardRect, g: GridArg, c: Cell): Rect {
  const { w, h } = cellSize(r, g);
  const x0 = r.cx - r.w / 2 + c.col * w;
  const y1 = r.cy + r.h / 2 - c.row * h;
  return { x0, y0: y1 - h, x1: x0 + w, y1 };
}

export function cellCenter(r: BoardRect, g: GridArg, c: Cell): { x: number; y: number } {
  const q = cellRect(r, g, c);
  return { x: (q.x0 + q.x1) / 2, y: (q.y0 + q.y1) / 2 };
}

export const screenToWorld = (vp: Viewport, sx: number, sy: number) => ({ x: sx - vp.width / 2, y: vp.height / 2 - sy });
export const worldToScreen = (vp: Viewport, x: number, y: number) => ({ x: x + vp.width / 2, y: vp.height / 2 - y });

export function pointToCell(r: BoardRect, grid: GridArg, x: number, y: number, clamp: boolean): Cell | null {
  const g = asGrid(grid);
  if (Number.isNaN(x) || Number.isNaN(y)) return null;
  if (!clamp && (!Number.isFinite(x) || !Number.isFinite(y))) return null;
  const { w, h } = cellSize(r, g);
  let col = Math.floor((x - (r.cx - r.w / 2)) / w);
  let row = Math.floor((r.cy + r.h / 2 - y) / h);
  if (!clamp) {
    if (x === r.cx + r.w / 2) col = g.cols - 1;
    if (y === r.cy - r.h / 2) row = g.rows - 1;
  }
  if (clamp) return { row: Math.min(g.rows - 1, Math.max(0, row)), col: Math.min(g.cols - 1, Math.max(0, col)) };
  if (row < 0 || col < 0 || row >= g.rows || col >= g.cols) return null;
  return { row, col };
}
